<template>
  <div class="min-h-screen bg-black text-white font-sans selection:bg-purple-500 selection:text-white overflow-x-hidden">
    <!-- Background Elements -->
    <div class="fixed inset-0 z-0 pointer-events-none">
        <div class="absolute top-[-20%] left-[-10%] w-[50%] h-[50%] bg-purple-900/20 rounded-full blur-[128px] animate-blob"/>
        <div class="absolute bottom-[-20%] right-[-10%] w-[50%] h-[50%] bg-pink-900/20 rounded-full blur-[128px] animate-blob animation-delay-2000"/>
        <div class="absolute inset-0 bg-[url('/grid.svg')] opacity-[0.05]"/>
        
        <!-- Floating Elements -->
        <img src="/running.svg" class="absolute top-[15%] right-[15%] w-24 h-24 opacity-10 animate-blob animation-delay-2000 rotate-12" alt="" >
        <img src="/shape_circle.svg" class="absolute bottom-[20%] left-[10%] w-32 h-32 opacity-5 animate-blob animation-delay-4000 -rotate-12" alt="" >
        <img src="/shape_square.svg" class="absolute top-[40%] left-[5%] w-16 h-16 opacity-10 animate-blob animation-delay-100 rotate-45" alt="" >
        <img src="/shape_t.svg" class="absolute bottom-[40%] right-[5%] w-20 h-20 opacity-5 animate-blob animation-delay-3000 -rotate-6" alt="" >
    </div>

    <!-- Hero Section -->
    <section class="relative z-10 min-h-screen flex flex-col items-center justify-center p-4 text-center sm:p-6">
        <!-- Brand -->
        <h1 class="mb-4 text-7xl md:text-9xl font-black tracking-tighter text-transparent bg-clip-text bg-gradient-to-br from-white via-gray-200 to-gray-500 animate-fade-in-up p-4">
            VIBE LABS
        </h1>
        
        <div class="mb-6 animate-fade-in-up animation-delay-100">
            <span class="inline-block px-3 py-1 bg-purple-900/30 border border-purple-500/30 rounded-full text-purple-300 text-xs font-bold uppercase tracking-wider">
                <template v-if="whatsNew">ALPHA {{ whatsNew.id }}</template>
                <template v-else>ALPHA</template>
            </span>
        </div>

        <p class="mb-8 text-xl md:text-3xl text-gray-400 font-light tracking-wide max-w-2xl mx-auto animate-fade-in-up animation-delay-100">
            <span class="text-purple-400 font-medium block mb-2">BE THE HERO OF GAME NIGHT.</span>
            It's bringing board game night to the digital era.
        </p>

        <!-- Main Action Card -->
        <div class="w-full max-w-md bg-gray-900/60 backdrop-blur-2xl border border-gray-800 rounded-3xl p-5 shadow-2xl animate-fade-in-up animation-delay-200 ring-1 ring-white/10 group transition-all duration-500 hover:ring-purple-500/50 sm:p-8">
            
            <!-- FEATURED GAMES (Hostless Mode) -->
            <div class="space-y-2.5">
                <button
                    class="group/btn relative w-full overflow-hidden rounded-2xl bg-gradient-to-r from-amber-600 to-red-600 px-4 py-2.5 text-white shadow-lg shadow-amber-500/20 transition-all hover:scale-[1.02] active:scale-[0.98] sm:px-5"
                    @click="playGame('BATTLE_OF_THE_VIEWS')"
                >
                    <div class="absolute inset-0 bg-white/10 opacity-0 group-hover/btn:opacity-100 transition-opacity"/>
                    <span class="relative z-10 grid min-h-[3rem] w-full grid-cols-[3.2rem_minmax(0,1fr)_3.2rem] items-center gap-2 sm:gap-3">
                        <span aria-hidden="true" class="block h-6 w-full" />
                        <div class="flex flex-col items-center justify-center">
                            <span class="[text-wrap:balance] text-center text-[clamp(1.05rem,4.2vw,1.6rem)] font-black leading-[1.05] tracking-[-0.02em]">PLAY BATTLE OF THE VIEWS</span>
                            <span v-if="getTagline('BATTLE_OF_THE_VIEWS')" class="mt-0.5 text-center text-[0.7rem] sm:text-xs font-semibold text-amber-100/90 tracking-wide uppercase">{{ getTagline('BATTLE_OF_THE_VIEWS') }}</span>
                        </div>
                        <div class="flex flex-col items-center justify-center">
                            <span class="text-xl sm:text-2xl leading-none">
                                <span v-if="loading === 'BATTLE_OF_THE_VIEWS'" class="animate-spin">⏳</span>
                                <span v-else>📊</span>
                            </span>
                            <div class="mt-1 flex flex-col items-center leading-none">
                                <span v-if="getSensorsForGame('BATTLE_OF_THE_VIEWS').length === 0" class="px-1.5 py-0.5 rounded-full bg-black/40 border border-white/20 text-[0.525rem] font-bold uppercase tracking-wider text-white/90 shadow-sm whitespace-nowrap">
                                    Touch
                                </span>
                                <span v-else v-for="sensor in getSensorsForGame('BATTLE_OF_THE_VIEWS')" :key="sensor" class="px-1.5 py-0.5 rounded-full bg-black/40 border border-white/20 text-[0.525rem] font-bold uppercase tracking-wider text-white/90 shadow-sm whitespace-nowrap">
                                    {{ getSensorBadgeInfo(sensor).shortLabel }}
                                </span>
                            </div>
                        </div>
                    </span>
                </button>

                <button 
                    class="group/btn relative w-full overflow-hidden rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-600 px-4 py-2.5 text-white shadow-lg shadow-emerald-500/20 transition-all hover:scale-[1.02] active:scale-[0.98] sm:px-5"
                    @click="playGame('TIME_ATTACK')"
                >
                    <div class="absolute inset-0 bg-white/10 opacity-0 group-hover/btn:opacity-100 transition-opacity"/>
                    <span class="relative z-10 grid min-h-[3rem] w-full grid-cols-[3.2rem_minmax(0,1fr)_3.2rem] items-center gap-2 sm:gap-3">
                        <span aria-hidden="true" class="block h-6 w-full" />
                        <div class="flex flex-col items-center justify-center">
                            <span class="[text-wrap:balance] text-center text-[clamp(1.05rem,4.2vw,1.6rem)] font-black leading-[1.05] tracking-[-0.02em]">PLAY TIME ATTACK</span>
                            <span v-if="getTagline('TIME_ATTACK')" class="mt-0.5 text-center text-[0.7rem] sm:text-xs font-semibold text-emerald-100/90 tracking-wide uppercase">{{ getTagline('TIME_ATTACK') }}</span>
                        </div>
                        <div class="flex flex-col items-center justify-center">
                            <span class="text-xl sm:text-2xl leading-none">
                                <span v-if="loading === 'TIME_ATTACK'" class="animate-spin">⏳</span>
                                <span v-else>⏱️</span>
                            </span>
                            <div class="mt-1 flex flex-col items-center leading-none">
                                <span v-if="getSensorsForGame('TIME_ATTACK').length === 0" class="px-1.5 py-0.5 rounded-full bg-black/40 border border-white/20 text-[0.525rem] font-bold uppercase tracking-wider text-white/90 shadow-sm whitespace-nowrap">
                                    Touch
                                </span>
                                <span v-else v-for="sensor in getSensorsForGame('TIME_ATTACK')" :key="sensor" class="px-1.5 py-0.5 rounded-full bg-black/40 border border-white/20 text-[0.525rem] font-bold uppercase tracking-wider text-white/90 shadow-sm whitespace-nowrap">
                                    {{ getSensorBadgeInfo(sensor).shortLabel }}
                                </span>
                            </div>
                        </div>
                    </span>
                </button>

                <button 
                    class="group/btn relative w-full overflow-hidden rounded-2xl bg-gradient-to-r from-pink-600 to-orange-600 px-4 py-2.5 text-white shadow-lg shadow-pink-500/20 transition-all hover:scale-[1.02] active:scale-[0.98] sm:px-5"
                    @click="playGame('SOUND_ALIKE')"
                >
                    <div class="absolute inset-0 bg-white/10 opacity-0 group-hover/btn:opacity-100 transition-opacity"/>
                    <span class="relative z-10 grid min-h-[3rem] w-full grid-cols-[3.2rem_minmax(0,1fr)_3.2rem] items-center gap-2 sm:gap-3">
                        <span aria-hidden="true" class="block h-6 w-full" />
                        <div class="flex flex-col items-center justify-center">
                            <span class="[text-wrap:balance] text-center text-[clamp(1.05rem,4.2vw,1.6rem)] font-black leading-[1.05] tracking-[-0.02em]">PLAY SOUND ALIKE</span>
                            <span v-if="getTagline('SOUND_ALIKE')" class="mt-0.5 text-center text-[0.7rem] sm:text-xs font-semibold text-pink-100/90 tracking-wide uppercase">{{ getTagline('SOUND_ALIKE') }}</span>
                        </div>
                        <div class="flex flex-col items-center justify-center">
                            <span class="text-xl sm:text-2xl leading-none">
                                <span v-if="loading === 'SOUND_ALIKE'" class="animate-spin">⏳</span>
                                <span v-else>🔊</span>
                            </span>
                            <div class="mt-1 flex flex-col items-center leading-none">
                                <span v-if="getSensorsForGame('SOUND_ALIKE').length === 0" class="px-1.5 py-0.5 rounded-full bg-black/40 border border-white/20 text-[0.525rem] font-bold uppercase tracking-wider text-white/90 shadow-sm whitespace-nowrap">
                                    Touch
                                </span>
                                <span v-else v-for="sensor in getSensorsForGame('SOUND_ALIKE')" :key="sensor" class="px-1.5 py-0.5 rounded-full bg-black/40 border border-white/20 text-[0.525rem] font-bold uppercase tracking-wider text-white/90 shadow-sm whitespace-nowrap">
                                    {{ getSensorBadgeInfo(sensor).shortLabel }}
                                </span>
                            </div>
                        </div>
                    </span>
                </button>

                <button 
                    class="group/btn relative w-full overflow-hidden rounded-2xl bg-gradient-to-r from-blue-600 to-purple-600 px-4 py-2.5 text-white shadow-lg shadow-purple-500/20 transition-all hover:scale-[1.02] active:scale-[0.98] sm:px-5"
                    @click="playGame('HOLE_IN_THE_WALL')"
                >
                    <div class="absolute inset-0 bg-white/10 opacity-0 group-hover/btn:opacity-100 transition-opacity"/>
                    <span class="relative z-10 grid min-h-[3rem] w-full grid-cols-[3.2rem_minmax(0,1fr)_3.2rem] items-center gap-2 sm:gap-3">
                        <span aria-hidden="true" class="block h-6 w-full" />
                        <div class="flex flex-col items-center justify-center">
                            <span class="[text-wrap:balance] text-center text-[clamp(1.05rem,4.2vw,1.6rem)] font-black leading-[1.05] tracking-[-0.02em]">PLAY HOLE IN THE WALL</span>
                            <span v-if="getTagline('HOLE_IN_THE_WALL')" class="mt-0.5 text-center text-[0.7rem] sm:text-xs font-semibold text-purple-100/90 tracking-wide uppercase">{{ getTagline('HOLE_IN_THE_WALL') }}</span>
                        </div>
                        <div class="flex flex-col items-center justify-center">
                            <span class="text-xl sm:text-2xl leading-none">
                                <span v-if="loading === 'HOLE_IN_THE_WALL'" class="animate-spin">⏳</span>
                                <span v-else>📷</span>
                            </span>
                            <div class="mt-1 flex flex-col items-center leading-none">
                                <span v-if="getSensorsForGame('HOLE_IN_THE_WALL').length === 0" class="px-1.5 py-0.5 rounded-full bg-black/40 border border-white/20 text-[0.525rem] font-bold uppercase tracking-wider text-white/90 shadow-sm whitespace-nowrap">
                                    Touch
                                </span>
                                <span v-else v-for="sensor in getSensorsForGame('HOLE_IN_THE_WALL')" :key="sensor" class="px-1.5 py-0.5 rounded-full bg-black/40 border border-white/20 text-[0.525rem] font-bold uppercase tracking-wider text-white/90 shadow-sm whitespace-nowrap">
                                    {{ getSensorBadgeInfo(sensor).shortLabel }}
                                </span>
                            </div>
                        </div>
                    </span>
                </button>

                <button
                    v-if="isAlphaMixUnlocked"
                    class="group/btn relative w-full overflow-hidden rounded-2xl bg-gradient-to-r from-yellow-500 to-yellow-700 px-4 py-2.5 text-white shadow-lg shadow-yellow-500/20 transition-all hover:scale-[1.02] active:scale-[0.98] sm:px-5"
                    @click="playGame('ALPHA_MIX_A')"
                >
                    <div class="absolute inset-0 bg-white/10 opacity-0 group-hover/btn:opacity-100 transition-opacity"/>
                    <span class="relative z-10 grid min-h-[3rem] w-full grid-cols-[3.2rem_minmax(0,1fr)_3.2rem] items-center gap-2 sm:gap-3">
                        <span aria-hidden="true" class="block h-6 w-full" />
                        <span class="[text-wrap:balance] text-center text-[clamp(1.05rem,4.2vw,1.6rem)] font-black leading-[1.05] tracking-[-0.02em]">PLAY ALPHA MIX</span>
                        <span class="flex h-7 w-7 items-center justify-center text-xl sm:text-2xl">
                            <span v-if="loading === 'ALPHA_MIX_A'" class="animate-spin">⏳</span>
                            <span v-else>🏆</span>
                        </span>
                    </span>
                </button>
                <div v-else class="relative w-full overflow-hidden rounded-2xl bg-gray-800/80 border border-gray-700 px-4 py-2.5 text-gray-500 transition-all sm:px-5 cursor-not-allowed">
                    <span class="relative z-10 grid min-h-[3rem] w-full grid-cols-[3.2rem_minmax(0,1fr)_3.2rem] items-center gap-2 sm:gap-3">
                        <span aria-hidden="true" class="block h-6 w-full" />
                        <div class="flex flex-col items-center justify-center gap-0.5">
                            <span class="[text-wrap:balance] text-center text-[clamp(1.05rem,4.2vw,1.6rem)] font-black leading-[1.05] tracking-[-0.02em]">PLAY ALPHA MIX</span>
                            <span class="text-[0.65rem] font-bold uppercase tracking-wider text-amber-500/80">Finish {{ completedCoreGamesCount > 0 ? `${completedCoreGamesCount}/4` : '4' }} Core Games to Unlock</span>
                        </div>
                        <span class="flex h-7 w-7 items-center justify-center text-xl sm:text-2xl opacity-50">
                            🔒
                        </span>
                    </span>
                </div>
            </div>

            <div v-if="developmentSessionTemplates.length > 0" class="mt-6 border-t border-gray-700/60 pt-4 space-y-2">
                <p class="text-left text-xs font-semibold uppercase tracking-[0.16em] text-amber-300/80">Development Sessions</p>
                <button
                    v-for="template in developmentSessionTemplates"
                    :key="template.id"
                    class="w-full py-3 px-4 bg-gray-800/80 border border-amber-500/30 text-amber-100 font-bold text-sm rounded-xl hover:bg-gray-700/80 hover:border-amber-400/50 transition-all flex items-center justify-between gap-3 text-left"
                    @click="playGame(template.id)"
                >
                    <div class="flex flex-col truncate gap-0.5">
                        <span class="truncate">{{ template.title }} <span class="text-amber-300/80">({{ template.roundCount }} rounds)</span></span>
                        <span v-if="template.tagline" class="text-xs font-medium text-amber-200/70 truncate">{{ template.tagline }}</span>
                        <div class="flex items-center gap-1 mt-0.5">
                            <span v-if="getSensorsForGame(template.id).length === 0" class="px-1.5 py-0.5 rounded-full bg-black/40 border border-white/10 text-[0.55rem] font-bold uppercase tracking-wider text-amber-200/90">
                                Touch
                            </span>
                            <span v-else v-for="sensor in getSensorsForGame(template.id)" :key="sensor" class="px-1.5 py-0.5 rounded-full bg-black/40 border border-white/10 text-[0.55rem] font-bold uppercase tracking-wider text-amber-200/90">
                                {{ getSensorBadgeInfo(sensor).shortLabel }}
                            </span>
                        </div>
                    </div>
                    <span v-if="loading === template.id" class="animate-spin">⏳</span>
                    <span v-else>🧪</span>
                </button>
            </div>

            <div v-if="upcomingScheduledEvents.length > 0" class="mt-6 border-t border-gray-700/60 pt-4 space-y-3">
                <p class="text-left text-xs font-semibold uppercase tracking-[0.16em] text-cyan-300/80">Upcoming Public Games</p>
                <NuxtLink
                    v-for="event in upcomingScheduledEvents"
                    :key="event.slug"
                    :to="`/events/${event.slug}`"
                    class="block rounded-2xl border border-slate-700 bg-slate-950/70 px-4 py-3 text-left transition-colors hover:border-cyan-400/60 hover:bg-slate-900/80"
                >
                    <div class="flex items-start justify-between gap-4">
                        <div>
                            <div class="font-bold text-white">{{ event.templateTitle }}</div>
                            <div class="mt-1 text-sm text-slate-300">{{ formatScheduledEventTime(event.startsAtUtc, event.timezone) }}</div>
                        </div>
                        <div class="rounded-full border border-cyan-400/30 px-3 py-1 text-xs font-bold uppercase tracking-[0.16em] text-cyan-300">
                            {{ formatCountdown(event.startsAtUtc) }}
                        </div>
                    </div>
                </NuxtLink>
            </div>

            <!-- Host Button (Developer / Config Only) -->
            <div v-if="showHostButton">
                <button 
                    class="w-full mb-8 py-3 bg-gray-800 text-gray-400 font-bold text-sm rounded-xl hover:bg-gray-700 hover:text-white transition-all flex items-center justify-center gap-2"
                    @click="startHosting"
                >
                    <span>🛠 HOST CUSTOM GAME</span>
                    <span v-if="loading === 'HOST'" class="animate-spin">⏳</span>
                </button>
            </div>

        </div>

        <!-- Scroll Indicator -->
        <div class="absolute bottom-10 animate-bounce text-gray-600">
            ↓ Scroll to explore
        </div>
    </section>

    <!-- Game Night Section (Planner Persona - Moved Up) -->
    <section class="relative z-10 py-24 px-6 border-t border-gray-900/50 bg-black/50 backdrop-blur-sm">
        <div class="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <div class="relative aspect-square md:aspect-[4/3] bg-gradient-to-bl from-gray-800 to-gray-900 rounded-3xl border border-gray-800 overflow-hidden shadow-2xl order-2 md:order-1 group">
                <img 
                    src="/mobile_concept.png" 
                    alt="Mobile Game UI" 
                    class="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
                >
                <div class="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent pointer-events-none"/>
            </div>
            <div class="space-y-6 order-1 md:order-2">
                <div class="inline-block px-3 py-1 bg-pink-900/30 border border-pink-500/30 rounded-full text-pink-300 text-xs font-bold uppercase tracking-wider">For The Planner</div>
                <h2 class="text-4xl md:text-6xl font-black tracking-tight">GAME NIGHT, UPGRADED.</h2>
                <p class="text-xl text-gray-400 leading-relaxed">
                    You send the invites and we take care of the memories.
                </p>
                <div class="grid grid-cols-2 gap-4 pt-4">
                    <div class="p-4 rounded-2xl bg-gray-900/50 border border-gray-800">
                        <div class="text-3xl mb-2">📱</div>
                        <div class="font-bold text-white">No App Needed</div>
                        <div class="text-sm text-gray-500">Just scan & play</div>
                    </div>
                    <div class="p-4 rounded-2xl bg-gray-900/50 border border-gray-800">
                        <div class="text-3xl mb-2">📺</div>
                        <div class="font-bold text-white">Big Screen Ready</div>
                        <div class="text-sm text-gray-500">Cast to TV easily</div>
                    </div>
                </div>
            </div>
        </div>
    </section>

    <!-- Streamer Section (Influencer Persona - Moved Down) -->
    <section class="relative z-10 py-24 px-6 border-t border-gray-900/50">
        <div class="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <div class="space-y-6">
                <div class="flex flex-wrap gap-2">
                    <div class="inline-block px-3 py-1 bg-purple-900/30 border border-purple-500/30 rounded-full text-purple-300 text-xs font-bold uppercase tracking-wider">For Influencers</div>
                    <div class="inline-block px-3 py-1 bg-cyan-900/30 border border-cyan-500/30 rounded-full text-cyan-300 text-xs font-bold uppercase tracking-wider">For Brands</div>
                </div>
                <h2 class="text-4xl md:text-6xl font-black tracking-tight">BRING THE CROWD LIVE.</h2>
                <p class="text-xl text-gray-400 leading-relaxed">
                    Contact the studio for live events, brand activations, and creator-led experiences built for communities and high-energy audience play.
                </p>
                <ul class="space-y-4 text-gray-300">
                    <li class="flex items-center gap-3">
                        <span class="flex items-center justify-center w-6 h-6 rounded-full bg-green-500/20 text-green-400">✓</span>
                        Creator and brand event support
                    </li>
                    <li class="flex items-center gap-3">
                        <span class="flex items-center justify-center w-6 h-6 rounded-full bg-green-500/20 text-green-400">✓</span>
                        Audience engagement for launches and activations
                    </li>
                    <li class="flex items-center gap-3">
                        <span class="flex items-center justify-center w-6 h-6 rounded-full bg-green-500/20 text-green-400">✓</span>
                        Shareable moments for socials and campaigns
                    </li>
                </ul>
            </div>
            <div class="relative aspect-video bg-gradient-to-tr from-gray-800 to-gray-900 rounded-3xl border border-gray-800 overflow-hidden shadow-2xl group cursor-default selection:bg-none">
                <img 
                    src="/streamer_concept.png" 
                    alt="Streamer Dashboard UI" 
                    class="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity duration-500"
                >
                
                <!-- Decorative UI Elements imitating OBS -->
                <div class="absolute top-4 left-4 flex gap-2 z-10">
                    <div class="w-3 h-3 rounded-full bg-red-500 shadow-lg shadow-red-500/20"/>
                    <div class="w-3 h-3 rounded-full bg-yellow-500 shadow-lg shadow-yellow-500/20"/>
                    <div class="w-3 h-3 rounded-full bg-green-500 shadow-lg shadow-green-500/20"/>
                </div>
            </div>
        </div>
    </section>

    <!-- Footer -->
    <footer class="relative z-10 border-t border-gray-800 bg-black/90 backdrop-blur-sm">
        <div class="max-w-5xl mx-auto px-6 py-14 grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8 text-sm">

            <!-- Column 1: Next Game -->
            <div>
                <h3 class="text-xs font-bold uppercase tracking-[0.16em] text-gray-400 mb-4">Community Events</h3>
                <template v-if="nextFooterEvent">
                    <NuxtLink
                        :to="`/events/${nextFooterEvent.slug}`"
                        class="block rounded-xl border border-gray-800 bg-gray-900/60 px-4 py-3 transition-colors hover:border-purple-500/50 hover:bg-gray-900"
                    >
                        <div class="font-bold text-white leading-tight">{{ nextFooterEvent.templateTitle }}</div>
                        <div class="mt-1.5 text-gray-400 text-xs">{{ formatScheduledEventTime(nextFooterEvent.startsAtUtc, nextFooterEvent.timezone) }}</div>
                        <div class="mt-2 inline-block rounded-full border border-purple-400/30 px-2.5 py-0.5 text-[0.65rem] font-bold uppercase tracking-[0.16em] text-purple-300">
                            {{ formatCountdown(nextFooterEvent.startsAtUtc) }}
                        </div>
                    </NuxtLink>
                </template>
                <template v-else>
                    <p class="text-gray-500 mb-3">No upcoming games scheduled.</p>
                    <button
                        class="text-purple-400 hover:text-purple-300 font-semibold transition-colors"
                        @click="scrollToTop"
                    >
                        Start one now →
                    </button>
                </template>
            </div>

            <!-- Column 2: What's New -->
            <div>
                <h3 class="text-xs font-bold uppercase tracking-[0.16em] text-gray-400 mb-4">What's New</h3>
                <div v-if="whatsNew" class="space-y-3">
                    <div class="text-[0.7rem] font-semibold uppercase tracking-[0.16em] text-purple-300">
                        {{ whatsNew.theme }}
                    </div>
                    <ul class="space-y-2.5 text-gray-300">
                        <li v-for="(item, idx) in whatsNew.highlights" :key="idx" class="flex items-start gap-2">
                            <span class="mt-0.5 text-purple-400">✦</span>
                            <span>{{ item }}</span>
                        </li>
                    </ul>
                    <div class="text-[0.65rem] font-semibold uppercase tracking-[0.16em] text-gray-600">
                        {{ whatsNew.id }} · {{ formatReleaseDate(whatsNew.releaseDate) }}
                    </div>
                    <NuxtLink
                        to="/releases"
                        class="inline-flex items-center gap-2 text-sm font-semibold text-purple-400 transition-colors hover:text-purple-300"
                    >
                        View all releases
                    </NuxtLink>
                </div>
                <div v-else class="text-gray-500">
                    No public release notes yet.
                </div>
            </div>

            <!-- Column 3: Connect -->
            <div>
                <h3 class="text-xs font-bold uppercase tracking-[0.16em] text-gray-400 mb-4">Connect</h3>
                <ul class="space-y-3">
                    <li>
                        <NuxtLink
                            to="/about"
                            class="flex items-center gap-2 text-gray-300 hover:text-white transition-colors"
                        >
                            <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
                            About
                        </NuxtLink>
                    </li>
                </ul>
            </div>

        </div>

        <!-- Copyright -->
        <div class="border-t border-gray-900 py-6 text-center text-xs text-gray-600">
            © {{ new Date().getFullYear() }} Vibe Labs. All rights reserved.
        </div>
    </footer>

  </div>
</template>

<script setup lang="ts">
import { getLatestShippedFeatureRelease } from '~/utils/featureReleaseCatalog'
import type { ScheduledGameSummary } from '~/types/scheduled-game'

const router = useRouter()
const { createGame } = useGameSession()
const config = useRuntimeConfig()
const route = useRoute()
const homepageTitle = 'The Party in Your Pocket'
const homepageDescription = 'Turn any gathering into a live game show. Start a room, invite friends, and run phone-first party games in seconds.'
const homepageImage = new URL('/images/mobile_mockup.png', config.public.siteUrl).toString()

type SessionTemplateSummary = {
    id: string
    title: string
    tagline?: string
    description: string
    status: 'ACTIVE' | 'DEVELOPMENT' | 'DELISTED'
    roundCount: number
    requiredSensors?: string[]
}

const { data: sessionTemplates } = useFetch<SessionTemplateSummary[]>('/api/session-templates', {
    default: () => [],
    server: false,
})
const { data: publicScheduledEvents } = useFetch<ScheduledGameSummary[]>('/api/scheduled-events', {
    default: () => [],
    server: false,
})

useHead({
  link: [
    { rel: 'canonical', href: config.public.siteUrl }
  ]
})

useSeoMeta({
  title: homepageTitle,
  description: homepageDescription,
  ogType: 'website',
  ogTitle: homepageTitle,
  ogDescription: homepageDescription,
  ogUrl: config.public.siteUrl,
  ogImage: homepageImage,
  ogImageAlt: 'Vibe Games mobile party game interface preview',
  twitterCard: 'summary_large_image',
  twitterTitle: homepageTitle,
  twitterDescription: homepageDescription,
  twitterImage: homepageImage,
  twitterImageAlt: 'Vibe Games mobile party game interface preview'
})


const loading = ref<string | null>(null) // Track which action is loading
const { getReferralContext } = useReferralAttribution()
const nowMs = ref(Date.now())
let homepageTimer: ReturnType<typeof setInterval> | null = null

const whatsNew = getLatestShippedFeatureRelease()

const nextFooterEvent = computed(() => {
    return upcomingScheduledEvents.value?.[0] ?? null
})

const scrollToTop = () => {
    globalThis.window?.scrollTo({ top: 0, behavior: 'smooth' })
}

// Logic to show host button: Config OR Query Param
const showHostButton = computed(() => {
    return config.public.allowHosting || route.query.host === 'true'
})

const developmentSessionTemplates = computed(() => {
    return (sessionTemplates.value || []).filter((template) => template.status === 'DEVELOPMENT')
})

const upcomingScheduledEvents = computed(() => {
    return (publicScheduledEvents.value || []).filter((event) => new Date(event.startsAtUtc).getTime() > nowMs.value)
})


const { track } = useAnalytics()
const { hasCompletedAllTemplates, hasCompletedTemplate, initProgression } = useGameProgression()

const coreGames = [
    'BATTLE_OF_THE_VIEWS',
    'TIME_ATTACK',
    'SOUND_ALIKE',
    'HOLE_IN_THE_WALL'
]

const DEFAULT_TAGLINES: Record<string, string> = {
    BATTLE_OF_THE_VIEWS: 'Internet Trivia',
    TIME_ATTACK: 'Precision Timing Drills',
    SOUND_ALIKE: 'Voice & Audio Mimicry',
    HOLE_IN_THE_WALL: 'Full-Body Sensor Action',
}

const getTagline = (templateId: string): string | undefined => {
    return sessionTemplates.value.find(t => t.id === templateId)?.tagline ?? DEFAULT_TAGLINES[templateId]
}

const GAME_SENSORS: Record<string, string[]> = {
    HOLE_IN_THE_WALL: ['camera'],
    SOUND_ALIKE: ['microphone'],
    TIME_ATTACK: [],
    BATTLE_OF_THE_VIEWS: [],
    TIGHT_ROPE: ['motion'],
}

const getSensorsForGame = (templateId: string): string[] => {
    const fromApi = sessionTemplates.value.find(t => t.id === templateId)?.requiredSensors
    return fromApi ?? GAME_SENSORS[templateId] ?? []
}

const getSensorBadgeInfo = (sensor: string) => {
    switch (sensor) {
        case 'camera':
            return { icon: '📷', shortLabel: 'Camera' }
        case 'microphone':
            return { icon: '🎤', shortLabel: 'Mic' }
        case 'motion':
            return { icon: '📱', shortLabel: 'Motion' }
        default:
            return { icon: '⚙️', shortLabel: sensor }
    }
}

const completedCoreGamesCount = computed(() => {
    return coreGames.filter(game => hasCompletedTemplate(game)).length
})

const isAlphaMixUnlocked = computed(() => {
    return completedCoreGamesCount.value >= coreGames.length
})

const formatScheduledEventTime = (startsAtUtc: string, timeZone: string) => {
    return new Intl.DateTimeFormat('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZone,
        timeZoneName: 'short',
    }).format(new Date(startsAtUtc))
}

const formatReleaseDate = (releaseDate: string) => {
    return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    }).format(new Date(releaseDate))
}

const formatCountdown = (startsAtUtc: string) => {
    const countdownSeconds = Math.floor((new Date(startsAtUtc).getTime() - nowMs.value) / 1000)
    if (countdownSeconds <= 0) return 'Live'
    const hours = Math.floor(countdownSeconds / 3600)
    if (hours >= 24) {
        const days = Math.floor(hours / 24)
        return `${days}d`
    }
    if (hours >= 1) return `${hours}h`
    const minutes = Math.max(1, Math.floor(countdownSeconds / 60))
    return `${minutes}m`
}

const safeTrackLandingClick = (templateId: string) => {
    const referral = getReferralContext()

    try {
        track('landing_cta_clicked', {
            template_id: templateId,
            referral_share_id: referral?.shareId,
            referral_template_id: referral?.templateId,
            referral_source_game_id: referral?.sourceGameId,
            acquisition_source: referral?.origin === 'recap' ? 'recap_referral' : undefined,
        })
    } catch (error) {
        console.error('[landing] Failed to track CTA click', error)
    }
}

const startHosting = async () => {
    loading.value = 'HOST'
    safeTrackLandingClick('HOST_CUSTOM')
    try {
        const newGameCode = await createGame(undefined, {
            referral: getReferralContext() || undefined,
        })
        await router.push(`/games/${newGameCode}/host`)
    } catch (e) {
        console.error(e)
        alert('Failed to start game server')
    } finally {
        loading.value = null
    }
}

const playGame = async (templateId: string) => {
    loading.value = templateId
    safeTrackLandingClick(templateId)
    try {
        const newGameCode = await createGame(templateId, {
            referral: getReferralContext() || undefined,
        })
        // For hostless games, we redirect to the PLAYER view
        // The creator is just another player.
        await router.push(`/games/${newGameCode}/players`)
    } catch (e: unknown) {
        console.error(e)
        const message = e instanceof Error ? e.message : 'Failed to start game'
        alert(message)
    } finally {
        loading.value = null
    }
}

onMounted(() => {
    initProgression()
    homepageTimer = setInterval(() => {
        nowMs.value = Date.now()
    }, 30_000)
})

onUnmounted(() => {
    if (homepageTimer) clearInterval(homepageTimer)
})

</script>

<style scoped>
@keyframes blob {
    0% { transform: translate(0px, 0px) scale(1); }
    33% { transform: translate(30px, -50px) scale(1.1); }
    66% { transform: translate(-20px, 20px) scale(0.9); }
    100% { transform: translate(0px, 0px) scale(1); }
}
.animate-blob {
    animation: blob 7s infinite;
}
.animation-delay-2000 {
    animation-delay: 2s;
}

@keyframes fade-in-up {
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}
.animate-fade-in-up {
    animation: fade-in-up 0.8s ease-out forwards;
    opacity: 0; 
}
.animation-delay-100 {
    animation-delay: 0.1s;
}
.animation-delay-200 {
    animation-delay: 0.2s;
}
</style>
